'use client';

import Link from 'next/link';
import {
  Home, Compass, Hash, Bell, Bookmark, Users, Sparkles,
  TrendingUp, Building2, Shield, Settings, LogOut,
  Brain
} from 'lucide-react';

interface FeedSidebarProps {
  collapsed: boolean;
}

const navGroups = [
  {
    id: 'main',
    label: 'Main',
    items: [
      { id: 'home', icon: Home, label: 'Home Feed', href: '/home-feed', active: true, badge: null },
      { id: 'discover', icon: Compass, label: 'Discover', href: '/home-feed', active: false, badge: null },
      { id: 'topics', icon: Hash, label: 'Topics', href: '/home-feed', active: false, badge: null },
      { id: 'notifications', icon: Bell, label: 'Notifications', href: '/home-feed', active: false, badge: '7' },
    ],
  },
  {
    id: 'you',
    label: 'You',
    items: [
      { id: 'bookmarks', icon: Bookmark, label: 'Bookmarks', href: '/home-feed', active: false, badge: null },
      { id: 'following', icon: Users, label: 'Following', href: '/home-feed', active: false, badge: null },
      { id: 'intent', icon: Brain, label: 'IntentGraph', href: '/home-feed', active: false, badge: null },
    ],
  },
  {
    id: 'create',
    label: 'Create',
    items: [
      { id: 'creator', icon: Sparkles, label: 'Creator Studio', href: '/home-feed', active: false, badge: 'AI' },
      { id: 'analytics', icon: TrendingUp, label: 'Analytics', href: '/home-feed', active: false, badge: null },
    ],
  },
  {
    id: 'explore',
    label: 'Explore',
    items: [
      { id: 'communities', icon: Users, label: 'Communities', href: '/home-feed', active: false, badge: null },
      { id: 'marketplace', icon: Building2, label: 'Marketplace', href: '/home-feed', active: false, badge: null },
      { id: 'trust', icon: Shield, label: 'Trust Center', href: '/home-feed', active: false, badge: null },
    ],
  },
];

export default function FeedSidebar({ collapsed }: FeedSidebarProps) {
  return (
    <aside
      className={`fixed left-0 top-14 bottom-0 z-40 flex flex-col border-r border-border bg-background/95 backdrop-blur-sm transition-all duration-300 ease-in-out overflow-hidden ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {navGroups.map((group) => (
          <div key={`navgroup-${group.id}`} className="mb-3">
            {/* Group label */}
            {!collapsed && (
              <div className="section-label px-3 mb-2 opacity-60">{group.label}</div>
            )}
            {collapsed && <div className="divider my-2 opacity-30" />}

            {group.items.map((item) => (
              <Link
                key={`nav-${item.id}`}
                href={item.href}
                className={`sidebar-nav-item ${item.active ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={18} className="flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span className={`text-xs font-600 px-1.5 py-0.5 rounded-md ${
                        item.badge === 'AI' ?'bg-cyan-400/15 text-cyan-400 border border-cyan-400/20' :'bg-primary/15 text-accent border border-primary/20'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            ))}
          </div>
        ))}
      </div>

      {/* Bottom actions */}
      <div className={`border-t border-border p-2 space-y-1 ${collapsed ? '' : ''}`}>
        <Link
          href="/home-feed"
          className={`sidebar-nav-item ${collapsed ? 'justify-center px-0' : ''}`}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={17} className="flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
        <Link
          href="/sign-up-login-screen"
          className={`sidebar-nav-item ${collapsed ? 'justify-center px-0' : ''}`}
          title={collapsed ? 'Sign out' : undefined}
        >
          <LogOut size={17} className="flex-shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </Link>
      </div>
    </aside>
  );
}